import { Injectable, NgZone } from '@angular/core';
import { PlatformService } from './platform.service';

/**
 * Version watchdog — ends the stale-tab class (Dave 2026-07-15, after the
 * NM "empty dashboard" saga): a long-lived SPA tab keeps running old code
 * forever, and no server-side deploy can fix a page that is already loaded.
 * This service polls index.html for the currently-served main-*.js hash and,
 * when it differs from the one this tab booted with, reloads the page at a
 * safe moment (next route change or after 30s of user idle) so every client
 * converges on the deployed version without ever being told to refresh.
 *
 * Failure-safe by design: any fetch/parse problem means "do nothing" — the
 * watchdog must never be able to break a working page.
 */
@Injectable({ providedIn: 'root' })
export class VersionWatchdogService {
  private static readonly POLL_MS = 5 * 60 * 1000;
  private static readonly IDLE_MS = 30 * 1000;
  // Once a reload is armed but deferred (form in progress), re-check this
  // often so the reload fires promptly once the form goes idle/saved rather
  // than waiting for the next 5-minute poll.
  private static readonly DEFER_RECHECK_MS = 5 * 1000;
  private bootBundle: string | null = null;
  private reloadArmed = false;
  private lastActivity = Date.now();

  /**
   * Dead-session incident (2026-07-17, KY /incentive): the watchdog's forced
   * `window.location.reload()` fired while an applicant was mid-form, killed
   * their session (auth/me + auth/refresh both 401 post-reload) and left the
   * form silently broken — 32 client-side upload failures before he happened
   * to log back in. A hard reload is exactly the wrong moment to force on a
   * dirty form: it can only ever destroy in-progress work (unsaved fields,
   * and File objects held for retry, which cannot survive a reload at all).
   *
   * A form component in progress registers a zero-arg predicate here
   * (returns true while there is something a reload would destroy — e.g.
   * `hasUnsavedChanges`) and clears it (pass null) on save/idle/destroy.
   * Conservative by design: only ONE guard is tracked at a time (the active
   * public form), defaults to "nothing to defer for" (null) so the watchdog
   * behaves exactly as before for every route that never registers one, and
   * a throwing guard is treated as "not dirty" (fail open toward the
   * watchdog's original failure-safe behavior) rather than wedging reload
   * forever on a broken predicate.
   */
  private deferralGuard: (() => boolean) | null = null;

  constructor(private zone: NgZone, private platformService: PlatformService) {}

  /** Called by an in-progress form's ngOnInit (and cleared in ngOnDestroy
   *  with `null`). While the guard returns true, an armed reload is deferred
   *  instead of firing — see class doc above. */
  setDeferralGuard(guard: (() => boolean) | null): void {
    this.deferralGuard = guard;
  }

  private isDeferred(): boolean {
    try {
      return !!this.deferralGuard && this.deferralGuard();
    } catch {
      // A broken predicate must never wedge the watchdog forever — fail
      // open (not deferred) rather than silently never reloading again.
      return false;
    }
  }

  start(): void {
    // Stale-tab detection makes no sense server-side — each SSR render is
    // already the freshest deployed code, and there is no long-lived tab to
    // watch. `document`/`window` are not globals under SSR either way.
    if (!this.platformService.isBrowser) return;
    this.bootBundle = this.extractBundle(document.documentElement.outerHTML)
      || this.currentScriptBundle();
    if (!this.bootBundle) return; // dev server / unexpected shell — stand down

    this.zone.runOutsideAngular(() => {
      ['click', 'keydown', 'scroll', 'touchstart'].forEach((ev) =>
        window.addEventListener(ev, () => (this.lastActivity = Date.now()), { passive: true }));
      setInterval(() => void this.check(), VersionWatchdogService.POLL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.check();
      });
    });
  }

  /** The bundle this document actually booted with (script tag src). */
  private currentScriptBundle(): string | null {
    const el = document.querySelector('script[src*="main-"]') as HTMLScriptElement | null;
    return el ? this.extractBundle(el.src) : null;
  }

  private extractBundle(text: string): string | null {
    const m = /main-[A-Z0-9]{8}\.js/.exec(text || '');
    return m ? m[0] : null;
  }

  private async check(): Promise<void> {
    if (this.reloadArmed || !this.bootBundle) return;
    try {
      const res = await fetch(`/index.html?wd=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const live = this.extractBundle(await res.text());
      if (live && live !== this.bootBundle) this.armReload();
    } catch {
      /* network hiccup — try again next poll */
    }
  }

  private armReload(): void {
    this.reloadArmed = true;
    const attempt = () => {
      if (this.isDeferred()) {
        // Form in progress — do not destroy it. Re-check soon; the reload
        // still fires as soon as the form clears its guard (saved/idle/left).
        setTimeout(attempt, VersionWatchdogService.DEFER_RECHECK_MS);
        return;
      }
      if (Date.now() - this.lastActivity >= VersionWatchdogService.IDLE_MS) {
        this.doReload();
      } else {
        setTimeout(attempt, 5000);
      }
    };
    setTimeout(attempt, 1000);
  }

  /** Isolated so specs can stub the actual navigation — window.location.reload
   *  is a non-configurable platform method in some browsers and can't be
   *  spied on directly; overriding this plain instance method can. */
  protected doReload(): void {
    window.location.reload();
  }
}
